import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, MenuItem, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosInstance';
import Profile from '../../components/Profile';
import Notification from '../../components/Notification';
import Papa from 'papaparse';
import LinearProgress from '@mui/material/LinearProgress';

const AddBookPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    bookTitle: '',
    bookDescription: '',
    authorName: '',
    publisherName: '',
    publicationYear: '',
    publisherEdition: '',
    ISBN: '',
    bookType: '',
    bookLanguage: '',
    coverImageURL: '',
    pdfURL: '',
    courseId: '',
    subjectId: '',
    semester: ''
  });

  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    axios.get('https://backend.scholarkart.in/api/courses').then((res) => setCourses(res.data));
  }, []);

  useEffect(() => {
    if (formData.courseId) {
      axios.get(`https://backend.scholarkart.in/api/subjects?courseId=${formData.courseId}`).then((res) => setSubjects(res.data));
    } else {
      setSubjects([]);
    }
  }, [formData.courseId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Minimal validation
    if (!formData.bookTitle || !formData.authorName || !formData.bookType || !formData.bookLanguage) {
      alert('Please fill all required fields.');
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user'));
      console.log("College ID:", user?.collegeId);
      
      const courseDetails = {};
      if (formData.courseId) courseDetails.courseId = formData.courseId;
      if (formData.subjectId) courseDetails.subjectId = formData.subjectId;
      if (formData.semester) courseDetails.semester = formData.semester;
      
      const bookPayload = {
        ...formData,
        courseDetails
      };

      delete bookPayload.courseId;
      delete bookPayload.subjectId;
      delete bookPayload.semester;

      await axios.post('https://backend.scholarkart.in/api/books', { ...bookPayload, collegeId: user.collegeId });
      navigate('/librarymanagement');
    } catch (error) {
      console.error('Error adding book:', error);
      alert('Failed to add book');
    }
  };

  const handleSearchBook = async (query) => {
    try {
      if (!query) {
        setSearchResults([]);
        return;
      }

      const user = JSON.parse(localStorage.getItem('user'));
      const token = localStorage.getItem('token');

      const response = await axios.get('https://backend.scholarkart.in/api/books/search-global', {
        params: { query, collegeId: user.collegeId },
        headers: { Authorization: `Bearer ${token}` }
      });

      setSearchResults(response.data || []);
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  const handleAddToLibrary = async (bookId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      await axios.post('https://backend.scholarkart.in/api/books/map-to-college', { bookId, collegeId: user.collegeId });
      alert('Book added to your library!');
      setSearchQuery('');
      setSearchResults([]);
      navigate('/librarymanagement');
    } catch (err) {
      console.error('Add to library error:', err);
    }
  };


  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const user = JSON.parse(localStorage.getItem("user"));
        const rawBooks = results.data;
  
        const errors = [];
        const validBooks = [];
  
        rawBooks.forEach((row, index) => {
          const missing = [];
          if (!row.bookTitle) missing.push("bookTitle");
          if (!row.authorName) missing.push("authorName");
          if (!row.bookType) missing.push("bookType");
          if (!row.bookLanguage) missing.push("bookLanguage");

  
          if (missing.length > 0) {
            errors.push({
              row: index + 2,
              reason: `Missing fields: ${missing.join(', ')}`
            });
          } else {
            const courseDetails = {};
            if (row.courseId) courseDetails.courseId = row.courseId;
            if (row.subjectId) courseDetails.subjectId = row.subjectId;
            if (row.semester) courseDetails.semester = row.semester;
            
            validBooks.push({
              ...row,
              courseDetails
            });
          }
        });
  
        if (validBooks.length === 0) {
          alert("No valid rows to upload.\n" + errors.map(e => `Row ${e.row}: ${e.reason}`).join('\n'));
          return;
        }
  
        try {
          setUploading(true);
          setProgress(10);
  
          const response = await axios.post('https://backend.scholarkart.in/api/books/bulk', {
            books: validBooks,
            collegeId: user.collegeId
          }, {
            onUploadProgress: (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgress(percent);
            }
          });
  
          setProgress(100);
  
          // Show summary
          const summary = [
            `✅ ${validBooks.length} book(s) uploaded successfully.`,
            `❌ ${errors.length} row(s) skipped due to validation issues.`
          ];
          if (errors.length > 0) {
            summary.push("\nDetails:\n" + errors.map(e => `Row ${e.row}: ${e.reason}`).join('\n'));
          }
  
          alert(summary.join('\n'));
        } catch (err) {
          console.error("Upload failed", err);
          alert("Upload failed");
        } finally {
          setUploading(false);
          setProgress(0);
        }
      }
    });
  };

  return (
    <main className="flex-grow p-1">
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-md">
        <div className="w-full max-w-lg" />
        <div className="flex items-center space-x-4">
          <Notification />
          <Profile />
        </div>
      </div>

      <Box p={4}>
        <Typography variant="h4" mb={4}>Search and Add Existing Book</Typography>

        <TextField
          label="Search from book collection"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            handleSearchBook(e.target.value);
          }}
          fullWidth
          sx={{ mb: 3 }}
        />
        {searchResults.length > 0 && (
          <Box mb={4}>
            {searchResults.map((book) => (
              <Box key={book._id} p={2} mb={1} border="1px solid #ccc" borderRadius={2}>
                <Typography><strong>{book.bookTitle}</strong> by {book.authorName}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => handleAddToLibrary(book._id)}
                >
                  Add to My Library
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <br />
        <Button variant="outlined" component="label">
          Bulk Upload CSV
          <input type="file" accept=".csv" hidden onChange={handleCSVUpload} />
        </Button>
        {uploading && (
            <Box mt={2}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="body2">{progress}%</Typography>
            </Box>
          )}
                  <Button
            variant="outlined"
            onClick={() => {
              const csvContent = `bookTitle,bookDescription,authorName,publisherName,publicationYear,publisherEdition,ISBN,bookType,bookLanguage,coverImageURL,pdfURL,courseId,subjectId,semester
          Sample Book,This is a demo book,Author Name,Publisher Inc,2024,First,1234567890123,Textbook,English,https://example.com/cover.jpg,https://example.com/book.pdf,courseIdHere,subjectIdHere,1`;
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = "book_upload_template.csv";
              link.click();
            }}
          >
            Download CSV Template
          </Button>
        <Typography variant="h4" mb={4}>Add New Book</Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Book Title" name="bookTitle" value={formData.bookTitle} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Author Name" name="authorName" value={formData.authorName} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Publisher Name" name="publisherName" value={formData.publisherName} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Publication Year" name="publicationYear" value={formData.publicationYear} onChange={handleChange} fullWidth type="number" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Edition" name="publisherEdition" value={formData.publisherEdition} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="ISBN" name="ISBN" value={formData.ISBN} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Book Type" name="bookType" value={formData.bookType} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Language" name="bookLanguage" value={formData.bookLanguage} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Book Description" name="bookDescription" value={formData.bookDescription} onChange={handleChange} fullWidth multiline rows={3} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Cover Image URL" name="coverImageURL" value={formData.coverImageURL} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="PDF URL" name="pdfURL" value={formData.pdfURL} onChange={handleChange} fullWidth />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Course"
                name="courseId"
                value={formData.courseId}
                onChange={handleChange}
                fullWidth
              >
                {courses.map((course) => (
                  <MenuItem key={course._id} value={course._id}>{course.courseName}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Subject"
                name="subjectId"
                value={formData.subjectId}
                onChange={handleChange}
                fullWidth
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject._id} value={subject._id}>{subject.subjectName}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Semester"
                name="semester"
                value={formData.semester}
                onChange={handleChange}
                fullWidth
              >
                {semesters.map((sem) => (
                  <MenuItem key={sem} value={sem}>Semester {sem}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary">Add Book to Library</Button>
            </Grid>
          </Grid>
        </form>

      </Box>
    </main>
  );
};

export default AddBookPage;
